import React, { useMemo } from 'react';
import { startsWith } from 'lodash';
import {
  Box,
  Button,
  CardActions,
  CardContent,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  Paper,
  Typography,
} from '@availity/element';
import { getSpecificEnv } from '@availity/env-var';

import { AvailityHeaderLogo, Footer } from '@/components';
import { useAppContext } from '@/context';

// handle getting the av portal urls
const { host = '' } = window.location;
const lowercaseHost = host?.toLowerCase();
// check if the page is loaded with '.net' and set the proper domain suffix
const isDotNet = lowercaseHost.indexOf('.net') >= 0;
const domainSuffix = isDotNet ? '.availity.net' : '.availity.com';

const currentEnv = getSpecificEnv();
const basePortalUrl =
  currentEnv?.toLowerCase() === 'prod' ? `https://apps${domainSuffix}` : `https://${currentEnv}-apps${domainSuffix}`;
const avHomePageUrl = `${basePortalUrl}/public/apps/home/#!/`;

const NextStepsOrgNeedsAdmin = () => {
  const { queryParams = {}, setCurrentNextStepsPage } = useAppContext();
  const { goto: contextGoTo } = queryParams;

  const gotoValueToUse = useMemo(() => {
    let output;
    if (contextGoTo) {
      // for safety, need to check that the goto url domain matches the window domain
      const { origin = '' } = window.top.location;
      output = startsWith(contextGoTo, origin) ? contextGoTo : basePortalUrl;
    } else {
      output = avHomePageUrl;
    }

    return output;
  }, [contextGoTo]);

  // ensure the correct location is used
  const handlePortalNavigation = () => {
    if (gotoValueToUse.indexOf('public/apps/home') >= 0) {
      window.top.location.href = gotoValueToUse;
    } else {
      window.location.href = gotoValueToUse;
    }
  };

  return (
    <Grid
      id="next-steps-org-needs-admin-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        size={{ xs: 12, sm: 12, md: 6 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        sx={{ minHeight: '100vh' }}
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Paper
            variant="elevation"
            elevation={6}
            sx={{ width: { xs: '350px', sm: '500px', md: '700px', lg: '1000px' } }}
          >
            <CardContent>
              <Typography variant="h1" mb={2}>
                Next Steps
              </Typography>
              <Typography variant="h2" mb={2}>
                If you are not the administrator
              </Typography>
              <Divider my={2} />
              <Typography variant="h3" my={2}>
                My Office Already Uses Availity Essentials
              </Typography>
              <Typography variant="subtitle1" mb={2}>
                You will not be able to use Availity Essentials until an administrator has registered your organization.
              </Typography>
              <List
                component="ul"
                sx={{
                  listStyleType: 'disc',
                  listStylePosition: 'outside',
                  paddingLeft: 3,
                  marginBottom: 3,
                }}
                disablePadding
              >
                <ListItem disableGutters disablePadding divider={false} sx={{ display: 'list-item' }}>
                  <Typography variant="subtitle1">
                    Identify your administrator and have them register your organization.
                  </Typography>
                  <Typography variant="subtitle1">
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                      Note: The administrator must complete personal ID verification during the registration process.
                    </Box>
                  </Typography>
                </ListItem>
                <ListItem disableGutters disablePadding divider={false} sx={{ display: 'list-item' }}>
                  <Typography variant="subtitle1">
                    Give your administrator your Availity Essentials User ID and ask to be added to your organization.
                  </Typography>
                </ListItem>
                <ListItem disableGutters disablePadding divider={false} sx={{ display: 'list-item' }}>
                  <Typography variant="subtitle1">
                    Once your administrator adds you to your organization, you can begin working in Availity Essentials.
                  </Typography>
                </ListItem>
              </List>
              <Box mb={3}>
                <Link
                  href="/availity/help-providers/index.html?av-contextId=cbe71d3a-d423-4ebe-8c54-ce6858500ad1"
                  target="_blank"
                  loadApp={false}
                >
                  Learn more about being an Availity Administrator
                </Link>
              </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'end' }}>
              <Button color="secondary" onClick={() => setCurrentNextStepsPage('initial')}>
                Back
              </Button>
              <Button color="primary" onClick={handlePortalNavigation}>
                Continue
              </Button>
            </CardActions>
          </Paper>
        </Grid>
        <Footer />
      </Grid>
    </Grid>
  );
};

export default NextStepsOrgNeedsAdmin;
